import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { OrgPanel } from '../components/OrgPanel'
import { ConfirmationModal } from '../components/ConfirmationModal'
import { FeatureEditorModal } from '../components/FeatureEditorModal'
import { ButtonLink } from '../components/Button'
import {
  useListOrganizationsQuery,
  useSetOrgStatusMutation,
  useSetOrgFeatureMutation,
  useSetOrgFeatureQuotaMutation,
  useListFeaturesQuery,
} from '@proaxiv/server-api'
import type { ListOrganizationsResponse, OrganizationSummary } from '@proaxiv/server-api'
import { AVAILABLE_FEATURES } from '../types'
import type { OrgFeatureMap } from '../types'
import type { OrgSummary } from '../components/OrgPanel'

type PendingAction =
  | { type: 'accept'; orgIds: string[] }
  | { type: 'reject'; orgIds: string[] }
  | { type: 'rejectWithMessage'; orgId: string }
  | { type: 'suspend'; orgIds: string[] }
  | null

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

/** Backend sentinel for "unlimited" — see {@code AdminController.SetQuotaBody} / {@code Integer.MAX_VALUE}. */
const UNLIMITED_QUOTA = 2147483647

interface FeatureQuotaInfo {
  id: string
  maxUsage: number
  currentUsage: number
}

interface ListFeaturesData {
  success: boolean
  features: FeatureQuotaInfo[]
}

/**
 * Builds an initial feature map from the org's enabled-features list plus, when available, the
 * live per-feature quota fetched from `GET /organizations/{orgId}/features`. Falls back to
 * `quota: 1` only while the quota request is in flight or for orgs with no quota data yet.
 */
function getInitialFeatures(org: OrganizationSummary, quotaData?: ListFeaturesData): OrgFeatureMap {
  const quotaByFeature = new Map((quotaData?.features ?? []).map((f) => [f.id, f]))
  const map: OrgFeatureMap = {}
  for (const feature of AVAILABLE_FEATURES) {
    const quotaInfo = quotaByFeature.get(feature)
    const quota: number | 'infinite' =
      quotaInfo == null
        ? 1
        : quotaInfo.maxUsage >= UNLIMITED_QUOTA
          ? 'infinite'
          : quotaInfo.maxUsage
    map[feature] = { enabled: org.enabledFeatures.includes(feature), quota }
  }
  return map
}

function toOrgSummary(org: OrganizationSummary): OrgSummary {
  return {
    id: org.orgId,
    name: org.name,
    description: org.description ?? '',
    createdAt: org.createdAt ?? '',
  }
}

/** Organization approval list — reads from the admin API and writes via status mutations. */
export function OrganizationApprovalsPage() {
  const { t } = useTranslation()

  const { data: rawData, isLoading, isError, refetch } = useListOrganizationsQuery({})
  const allOrgs: OrganizationSummary[] =
    (rawData as ListOrganizationsResponse | undefined)?.organizations ?? []

  const pendingOrgs = allOrgs.filter((o) => o.status === 'PENDING')
  const acceptedOrgs = allOrgs.filter(
    (o) => o.status === 'ACCEPTED' || o.status === 'ENABLED' || o.status === 'INACTIVE'
  )
  const rejectedOrgs = allOrgs.filter((o) => o.status === 'REJECTED')

  const [setOrgStatus] = useSetOrgStatusMutation()
  const [setOrgFeature] = useSetOrgFeatureMutation()
  const [setOrgFeatureQuota] = useSetOrgFeatureQuotaMutation()

  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [featureConfigs, setFeatureConfigs] = useState<Record<string, OrgFeatureMap>>({})
  const [editingFeaturesForId, setEditingFeaturesForId] = useState<string | null>(null)

  // Live per-feature quota for the org currently being edited — the org list/summary endpoint
  // only carries `enabledFeatures` (no quota), so the modal needs this to show real values on open.
  const { data: editingQuotaData } = useListFeaturesQuery(
    { orgId: editingFeaturesForId ?? '' },
    { skip: !editingFeaturesForId }
  )

  async function handleConfirm(result: { notify: boolean; message?: string }) {
    if (!pendingAction) return

    if (pendingAction.type === 'accept') {
      await Promise.all(
        pendingAction.orgIds.map((orgId) =>
          setOrgStatus({
            orgId,
            orgStatusBody: { status: 'ACCEPTED', notify: result.notify },
          })
        )
      )
    } else if (pendingAction.type === 'reject') {
      await Promise.all(
        pendingAction.orgIds.map((orgId) =>
          setOrgStatus({
            orgId,
            orgStatusBody: { status: 'REJECTED', notify: result.notify },
          })
        )
      )
    } else if (pendingAction.type === 'rejectWithMessage') {
      await setOrgStatus({
        orgId: pendingAction.orgId,
        orgStatusBody: {
          status: 'REJECTED',
          message: result.message?.trim() || undefined,
          notify: result.notify,
        },
      })
    } else if (pendingAction.type === 'suspend') {
      await Promise.all(
        pendingAction.orgIds.map((orgId) =>
          setOrgStatus({
            orgId,
            orgStatusBody: { status: 'PENDING', notify: result.notify },
          })
        )
      )
    }

    setPendingAction(null)
  }

  async function handleReopen(orgIds: string[]) {
    await Promise.all(
      orgIds.map((orgId) => setOrgStatus({ orgId, orgStatusBody: { status: 'PENDING' } }))
    )
  }

  function getModalTitle(): string {
    if (!pendingAction) return ''
    if (pendingAction.type === 'accept') {
      const count = pendingAction.orgIds.length
      return count === 1 ? t('modal_approve_one') : t('modal_approve_many', { count })
    }
    if (pendingAction.type === 'suspend') {
      const count = pendingAction.orgIds.length
      return count === 1 ? t('modal_suspend_one') : t('modal_suspend_many', { count })
    }
    return t('modal_reject')
  }

  async function handleSaveFeatures(orgId: string, features: OrgFeatureMap) {
    setFeatureConfigs((prev) => ({ ...prev, [orgId]: features }))
    setEditingFeaturesForId(null)
    const calls: Promise<unknown>[] = []
    for (const feature of AVAILABLE_FEATURES) {
      const cfg = features[feature] ?? { enabled: false, quota: 1 }
      calls.push(setOrgFeature({ orgId, feature, setFeatureBody: { enabled: cfg.enabled } }))
      if (cfg.enabled) {
        const quotaBody = { maxUsage: cfg.quota === 'infinite' ? UNLIMITED_QUOTA : cfg.quota }
        calls.push(
          setOrgFeatureQuota({
            orgId,
            feature,
            setQuotaBody: quotaBody,
          })
        )
      }
    }
    await Promise.all(calls)
    refetch()
  }

  const editingOrg = editingFeaturesForId
    ? allOrgs.find((o) => o.orgId === editingFeaturesForId)
    : null

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-6 md:py-8">
      {/* Breadcrumb */}
      <Link
        to="/organizations"
        className="text-pax-gray hover:text-pax-blue flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        {t('nav_back')}
      </Link>

      {/* Page title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-pax-black text-2xl font-bold tracking-tight md:text-3xl">
          {t('page_title')}
        </h1>
        <div className="flex items-center gap-3">
          <ButtonLink variant="primary" to="/organizations/new" leftIcon={<PlusIcon />}>
            New Organization
          </ButtonLink>
        </div>
      </div>

      {isLoading && <p className="text-pax-gray text-sm">{t('loading')}</p>}
      {isError && <p className="text-sm text-red-500">{t('error_load_orgs')}</p>}

      {/* Panels — both derived from the API list */}
      {!isLoading && !isError && (
        <>
          <OrgPanel
            title={t('panel_pending')}
            orgs={pendingOrgs.map(toOrgSummary)}
            bulkAcceptLabel={t('bulk_accept')}
            bulkRejectLabel={t('bulk_reject')}
            onBulkAccept={(ids) => setPendingAction({ type: 'accept', orgIds: ids })}
            onBulkReject={(ids) => setPendingAction({ type: 'reject', orgIds: ids })}
            onApprove={(id) => setPendingAction({ type: 'accept', orgIds: [id] })}
            onReject={(id) => setPendingAction({ type: 'reject', orgIds: [id] })}
            onRejectWithMessage={(id) => setPendingAction({ type: 'rejectWithMessage', orgId: id })}
          />

          <OrgPanel
            title={t('panel_accepted')}
            orgs={acceptedOrgs.map(toOrgSummary)}
            approveLabel={t('btn_suspend')}
            approveVariant="danger"
            bulkAcceptLabel={t('btn_suspend')}
            bulkAcceptVariant="danger"
            onBulkAccept={(ids) => setPendingAction({ type: 'suspend', orgIds: ids })}
            onApprove={(id) => setPendingAction({ type: 'suspend', orgIds: [id] })}
            onEditFeatures={(id) => setEditingFeaturesForId(id)}
          />

          <OrgPanel
            title={t('panel_rejected')}
            orgs={rejectedOrgs.map(toOrgSummary)}
            approveLabel={t('btn_reopen')}
            bulkAcceptLabel={t('btn_reopen')}
            onBulkAccept={(ids) => void handleReopen(ids)}
            onApprove={(id) => void handleReopen([id])}
          />
        </>
      )}

      {/* Confirmation modal */}
      {pendingAction && (
        <ConfirmationModal
          title={getModalTitle()}
          // Reject-with-message defaults to notifying (checkbox starts checked, still toggleable).
          notify={pendingAction.type === 'accept' || pendingAction.type === 'rejectWithMessage'}
          rejectMessage={pendingAction.type === 'rejectWithMessage' ? '' : undefined}
          onConfirm={handleConfirm}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {/* Feature editor modal — wait for the quota GET to settle (or reuse an
          already-saved draft) before mounting, otherwise its one-shot
          useState initializer locks in the quota:1 fallback and never syncs
          once the real maxUsage arrives. */}
      {editingFeaturesForId &&
        editingOrg &&
        (featureConfigs[editingFeaturesForId] != null || editingQuotaData != null) && (
          <FeatureEditorModal
            orgName={editingOrg.name}
            initialFeatures={
              featureConfigs[editingFeaturesForId] ??
              getInitialFeatures(editingOrg, editingQuotaData as ListFeaturesData | undefined)
            }
            onSave={(features) => void handleSaveFeatures(editingFeaturesForId, features)}
            onCancel={() => setEditingFeaturesForId(null)}
          />
        )}
    </div>
  )
}
